import { Appointment } from "@/features/appointments/types/Appointment";
import { useNutritionistConsult } from "@/features/experts/hooks/useNutritionistConsult";
import { useSpecialistConsult } from "@/features/experts/hooks/useSpecialistConsult";
import { useHealthAssessment } from "@/features/health-assessment/hooks/useHealthAssessment";
import { useDnaTracker } from "@/features/holifya-tests/dna-test/hooks/useDnaTracker";
import { DnaTestTrackingStatus } from "@/features/holifya-tests/dna-test/types/DnaTestTrackingStatus";
import { useGetDocuments } from "@/features/ocr/hooks/useGetDocuments";
import { useConsultsStore } from "@/utils/stores/useConsultsStore";
import { useNavigate } from "react-router-dom";

export const useSubNutritionWidget = () => {
    const { dnaTracker, dnaOrderId, updateTracker } = useDnaTracker()
    const navigate = useNavigate()
    const { isHealthAssessmentCompleted } = useHealthAssessment()
    const healthAssessmentCompleted = isHealthAssessmentCompleted()
    const { data: documents } = useGetDocuments();
    const filesUploaded = documents !== undefined && documents.length !== 0
    const { shouldPushBooking } = useSpecialistConsult()
    const { shouldPushNutritionistBooking, nutriConsultsLeft } = useNutritionistConsult()
    const { consultsData } = useConsultsStore()
    const specialistConsultsLeft = consultsData.specialistConsultsLeft
    const nutritionistConsultsLeft = consultsData.nutritionistConsultsLeft

    type DnaWidgetConfig = {
        text: string;
        buttonText: string;
        expertRole?: string,
        consult?: Appointment,
        onClick: () => void;
        variant?: "default" | "success"; // You can expand this if needed
    };

    const earlyStatuses: DnaTestTrackingStatus[] = [
        DnaTestTrackingStatus.Purchased,
        DnaTestTrackingStatus.Delivered,
        DnaTestTrackingStatus.TestDone,
        DnaTestTrackingStatus.SampleRegistered,
        DnaTestTrackingStatus.PickupRequested,
    ];

    const inProgressStatuses: DnaTestTrackingStatus[] = [
        DnaTestTrackingStatus.Departed,
        DnaTestTrackingStatus.DeliveredToLaboratory,
        DnaTestTrackingStatus.StartingToExtract,
        DnaTestTrackingStatus.StartingAnalysis,
        DnaTestTrackingStatus.AnalysisDone,
    ];

    const configMap: Partial<Record<DnaTestTrackingStatus, DnaWidgetConfig>> = {
        [DnaTestTrackingStatus.Purchased]: {
            text: "📦 Hai ricevuto il kit?",
            buttonText: "Sì, ricevuto",
            onClick: () => updateTracker(DnaTestTrackingStatus.Delivered, dnaOrderId!),
        },
        [DnaTestTrackingStatus.Delivered]: {
            text: "🧬 Effettua il test del DNA",
            buttonText: "Effettua",
            onClick: () => navigate("/dna-test/tutorial"),
        },
        [DnaTestTrackingStatus.TestDone]: {
            text: "🔐 Registra il Codice campione",
            buttonText: "Registra",
            onClick: () => navigate("/dna-test/register-swab-code"),
        },
        [DnaTestTrackingStatus.SampleRegistered]: {
            text: "🚛 Richiedi il ritiro del campione",
            buttonText: "Richiedi",
            onClick: () => navigate("/dna-test/packing"),
        },
        [DnaTestTrackingStatus.PickupRequested]: {
            text: "🚛 Modifica il ritiro del campione",
            buttonText: "Modifica",
            onClick: () => navigate("/dna-test/request-pickup"),
        },
        [DnaTestTrackingStatus.Departed]: {
            text: "🚛 Verso il laboratorio",
            buttonText: "",
            onClick: () => { },
        },
        [DnaTestTrackingStatus.DeliveredToLaboratory]: {
            text: "📍 Arrivato in laboratorio",
            buttonText: "",
            onClick: () => { },
        },
        [DnaTestTrackingStatus.StartingToExtract]: {
            text: "🧪 Inizio estrazione",
            buttonText: "",
            onClick: () => { },
        },
        [DnaTestTrackingStatus.StartingAnalysis]: {
            text: "🔬 In analisi",
            buttonText: "",
            onClick: () => { },
        },
        [DnaTestTrackingStatus.AnalysisDone]: {
            text: "🧬 Risultati in arrivo",
            buttonText: "",
            onClick: () => { },
        }
    };

    if (earlyStatuses.includes(dnaTracker as DnaTestTrackingStatus)) {
        const config = configMap[dnaTracker as DnaTestTrackingStatus];
        if (config) return { ...config, variant: "default" };
    }

    if (!healthAssessmentCompleted) {
        return {
            text: "📝 Rispondi all'Health Assessment",
            buttonText: "Rispondi",
            onClick: () => navigate("/health-assessment-home-page"),
            variant: "default",
        };
    }

    // 3. Then file upload
    if (!filesUploaded) {
        return {
            text: "📄 Carica i tuoi referti",
            buttonText: "Carica",
            onClick: () => navigate("/reports"),
            variant: "default",
        };
    }

    if (inProgressStatuses.includes(dnaTracker as DnaTestTrackingStatus)) {
        const config = configMap[dnaTracker as DnaTestTrackingStatus];
        if (config) return { ...config, variant: "default" };
    }

    const defaultReturn = shouldPushBooking && specialistConsultsLeft && specialistConsultsLeft > 0 ? {
        text: "👩‍⚕️️ Prenota consulto con medico",
        buttonText: "Prenota",
        onClick: () => { navigate("/equipe/your-experts") },
        variant: "default",
    } : shouldPushNutritionistBooking && nutritionistConsultsLeft && nutritionistConsultsLeft > 0 ? {
        text: "👨‍⚕️️ Prenota consulto con nutrizionista",
        buttonText: "Prenota",
        onClick: () => { navigate("/equipe/your-experts") },
        variant: "default",
    } : nutriConsultsLeft.length > 0 ? {
        text: `${nutriConsultsLeft[0].expert.data.name} ${nutriConsultsLeft[0].expert.data.family_name}`,
        buttonText: "Connettiti",
        expertRole: nutriConsultsLeft[0].expert.role,
        consult: nutriConsultsLeft[0],
        onClick: () => { window.open(nutriConsultsLeft[0].connectUrl, "_blank") },
        variant: "default",
    } : {
        text: "📝 Rispondi al check-up",
        buttonText: "Rispondi",
        onClick: () => { navigate("/check-up-assessment-home-page") },
        variant: "default",
    }

    const config = configMap[dnaTracker as DnaTestTrackingStatus];

    return {
        ...config ?? defaultReturn,
        variant: config?.variant ?? "default",
    };
}